import {RefObject, useCallback, useEffect, useRef, useState} from 'react';
import {defineMessages, useIntl} from 'react-intl';
import {paperclip, send} from '@sail/icons/react/Icon';
import {css, Divider, Icon, view, Spinner} from '@sail/ui/deprecated';
import {animate as sailAnimate} from '@sail/engine/deprecated';
import {AnimatePresence} from '@sail/engine';
import {useAnalytics} from '../AnalyticsProvider';
import {ANIMATION_DURATION} from '../animation';
import {useTheme} from '../ThemeProvider';
import {useIsMobileBreakpoints} from '../helpers/breakpoints';
import {InputNext} from './InputNext';
import {InputDotCom} from './InputDotCom';
import {InputProps} from './InputProps';
import {
  useNewUniversalHelpDesign,
  useUniversalHelpDotComDesign,
} from '../FeatureFlagsProvider';
import {StagedFileList, UploadErrorOverlay} from '../FileStaging/StagedFiles';

const messages = defineMessages({
  sendMessageAriaLabel: {
    id: 'conversations_ui.chat.input.sendButton.ariaLabel',
    description: 'Label for the chat textbox send button',
    defaultMessage: 'Send message',
  },
  selectAttachmentAriaLabel: {
    id: 'conversations_ui.chat.input.selectAttachment.ariaLabel',
    description: 'Label for the chat textbox select attachment',
    defaultMessage: 'Select attachment',
  },
});

export const HiddenFileInput = ({
  onChange,
  fileInputRef,
}: {
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  fileInputRef: RefObject<HTMLInputElement>;
}): JSX.Element => (
  <view.input
    data-testid="file-input"
    type="file"
    value=""
    onChange={onChange}
    css={{display: css.value('none')}}
    ref={fileInputRef}
  />
);

export const AttachmentButton = ({
  onSelect,
  handleFileInputKeyDown,
  fileInputRef,
}: {
  onSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleFileInputKeyDown: (e: React.KeyboardEvent) => void;
  fileInputRef: RefObject<HTMLInputElement>;
}): JSX.Element => {
  const intl = useIntl();

  return (
    <view.label
      aria-label={intl.formatMessage(messages.selectAttachmentAriaLabel)}
      css={{
        border: css.value('none'),
        background: css.value('none'),
        display: css.value('flex'),
        cursor: css.value('pointer'),
        paddingY: 'xsmall',
      }}
      tabIndex={0}
      onKeyDown={handleFileInputKeyDown}
    >
      <Icon
        css={{verticalAlign: css.value('center')}}
        icon={paperclip}
        data-testid="attachment-icon"
      />
      <HiddenFileInput onChange={onSelect} fileInputRef={fileInputRef} />
    </view.label>
  );
};

export const SendButton = ({
  onClick,
  disabled,
  pending,
}: {
  onClick: (arg1: React.SyntheticEvent) => void;
  disabled?: boolean;
  pending?: boolean;
}): JSX.Element => {
  const intl = useIntl();

  return (
    <view.button
      aria-label={intl.formatMessage(messages.sendMessageAriaLabel)}
      data-testid="send-button"
      disabled={disabled}
      onClick={onClick}
      css={{
        border: css.value('none'),
        background: css.value('none'),
        display: css.value('flex'),
        cursor: css.value(disabled ? 'auto' : 'pointer'),
        paddingY: 'xsmall',
      }}
      type="button"
    >
      {pending ? (
        <Spinner />
      ) : (
        <Icon
          css={{verticalAlign: css.value('center')}}
          icon={send}
          data-testid="send-icon"
        />
      )}
    </view.button>
  );
};

const MIN_INPUT_HEIGHT = 56;
const MAX_INPUT_HEIGHT = 140;

const DIVIDER_OFFSET = 1;
const MARGIN = 1;
const MARGIN_OFFSET = MARGIN * 2;

function InputCurrent({
  disabled,
  pending,
  sendMessageDisabled,
  attachmentDisabled,
  stagedFiles,
  onRemoveStagedFile,
  uploadErrors,
  hidden,
  animate,
  placeholder,
  onSendMessage,
  onSendFile,
  onSendTyping,
  focusInput,
}: InputProps) {
  const [enteredText, setEnteredText] = useState('');
  const hasStagedFiles = (stagedFiles?.length ?? 0) > 0;
  const isUploading = stagedFiles?.some((f) => f.status === 'uploading');
  const analytics = useAnalytics();
  const theme = useTheme();

  const isMobile = useIsMobileBreakpoints();

  const isSendDisabled =
    disabled || sendMessageDisabled || (enteredText === '' && !hasStagedFiles);

  const textareaStyles = [];
  if (isMobile) {
    textareaStyles.push(
      css({
        // less than 16px causes iOS to zoom on focus, which breaks the experience
        fontSize: css.value('16px'),
      }),
    );
  }

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const inputContainer = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const resizeTextArea = useCallback(() => {
    const textarea = textareaRef.current;
    const container = inputContainer.current;

    // important to include hidden here if nothing else to
    // trigger this to re-run when the input is re-rendered
    // after being un-hidden, otherwise it has scrollbars
    if (!hidden && container && textarea) {
      container.style.height = `${MIN_INPUT_HEIGHT + DIVIDER_OFFSET}px`;

      const textAreaContentsHeight = textarea.scrollHeight;

      const cappedTextAreaHeight =
        Math.min(
          MAX_INPUT_HEIGHT,
          Math.max(MIN_INPUT_HEIGHT, textAreaContentsHeight),
        ) +
        DIVIDER_OFFSET +
        MARGIN_OFFSET;

      container.style.height = `${cappedTextAreaHeight}px`;
    }
  }, [hidden, textareaRef, inputContainer]);

  const sendMessage = () => {
    if (!isSendDisabled && !isUploading) {
      const messageBody = enteredText.trim();
      setEnteredText('');

      if (messageBody || hasStagedFiles) {
        onSendMessage(messageBody);
      }
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent): void => {
    // Note: We are intentionally using a deprecated property, keyCode, in support for IME(Input Method Editor) Enters.
    // Why: For some languages like Japanese, IME is used to transform qwerty inputs into characters, and the user needs to hit Enter to select.
    // However, we are also looking to send the user message on Enter. So we need a way to differentiate a normal Enter vs IME Enter.
    // In the deprecated properties like 'keyCode' and 'which', the two Enters are represented with codes '13' (normal) and '229' (IME).
    // In nondeprecated properties like 'key' and 'code', both are represented as 'Enter'.
    // Since keyCode is still supported in majority of the browsers today, and the legacy Copilot input used keyCode for the longest time, we'll continue to use keyCode here for now.
    if (event.keyCode === 13 && !event.shiftKey) {
      event.preventDefault();

      sendMessage();
    }
  };

  const handleSendButtonClick = (event: React.SyntheticEvent): void => {
    event.preventDefault();

    sendMessage();
  };

  const handleChange = (
    event: React.ChangeEvent<HTMLTextAreaElement>,
  ): void => {
    const newEnteredText = event.target.value;
    setEnteredText(newEnteredText);
    if (newEnteredText.length) {
      onSendTyping();
    }
  };

  const handleFileSelection = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    analytics.action('file_attachment_sent');
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    e.target.files && onSendFile && onSendFile(e.target.files[0]);
  };

  const handleFileInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'Enter') return;
    if (!fileInputRef.current) return;
    fileInputRef.current.click();
  };

  useEffect(() => {
    resizeTextArea();
  }, [enteredText, resizeTextArea]);

  useEffect(() => {
    if (disabled || hidden || !focusInput) {
      return undefined;
    }

    const frame = requestAnimationFrame(() => textareaRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [disabled, focusInput, hidden]);

  const showSendButton =
    enteredText || !onSendFile || attachmentDisabled || hasStagedFiles;

  return (
    <>
      {stagedFiles && onRemoveStagedFile && stagedFiles.length > 0 ? (
        <StagedFileList
          stagedFiles={stagedFiles}
          onRemoveStagedFile={onRemoveStagedFile}
        />
      ) : null}
      <AnimatePresence>
        {hidden ? null : (
          <view.div
            data-testid="text-input-container"
            ref={inputContainer}
            css={{
              position: css.value('relative'),
              flexGrow: css.value(0),
              flexShrink: css.value(0),
              height: css.value(
                `${MIN_INPUT_HEIGHT + DIVIDER_OFFSET + MARGIN_OFFSET}px`,
              ),
              stack: 'y',
            }}
            uses={
              animate
                ? [
                    sailAnimate.in({
                      duration: ANIMATION_DURATION,
                      transform: 'translateY(calc(100% * 1))',
                      easing: 'ease-out',
                    }),
                    sailAnimate.out({
                      duration: ANIMATION_DURATION,
                      transform: 'translateY(calc(120% * 1))',
                      easing: 'ease-in',
                    }),
                  ]
                : []
            }
          >
            <UploadErrorOverlay uploadErrors={uploadErrors} />
            <Divider />
            <view.textarea
              data-testid="text-input"
              disabled={disabled}
              value={enteredText}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              aria-label={placeholder}
              ref={textareaRef}
              uses={textareaStyles}
              css={{
                resize: css.value('none'),
                border: css.value('none'),
                margin: MARGIN,
                padding: 250,
                paddingRight: css.value('52px'),
                position: css.value('absolute'),
                top: DIVIDER_OFFSET,
                left: 0,
                right: 0,
                bottom: 0,
                boxSizing: css.value('border-box'),
                boxShadow: 'none',
                borderBottomLeftRadius: 'medium',
                borderBottomRightRadius: 'medium',
                lineHeight: css.value('20px'),
                whiteSpace: css.value('normal'),
                '&:focus-visible': {
                  outline: theme.focusVisibleOutline,
                },
              }}
            />
            <view.div
              data-testid="text-input-icon-container"
              css={{
                position: css.value('absolute'),
                bottom: 'medium',
                right: 'medium',
                padding: 1,
                stack: 'x',
                gap: 'xsmall',
              }}
            >
              {showSendButton ? (
                <SendButton
                  onClick={handleSendButtonClick}
                  disabled={isSendDisabled || isUploading}
                  pending={pending}
                />
              ) : (
                <AttachmentButton
                  onSelect={handleFileSelection}
                  handleFileInputKeyDown={handleFileInputKeyDown}
                  fileInputRef={fileInputRef}
                />
              )}
            </view.div>
          </view.div>
        )}
      </AnimatePresence>
    </>
  );
}

export function Input(props: InputProps) {
  const useNewDesign = useNewUniversalHelpDesign();
  const useDotComDesign = useUniversalHelpDotComDesign();

  if (useNewDesign) {
    return <InputNext {...props} />;
  } else if (useDotComDesign) {
    return <InputDotCom {...props} />;
  }

  return <InputCurrent {...props} />;
}
